import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AuthenticationService } from '../../../shared/authentication/authentication.service';
import { BuilderSaveApi } from '../../../shared/Builder/Api/BuilderSaveApi';
import { BasePage } from '../../../shared/components/base-page.component';
import { ColorInputComponent } from '../../../shared/components/color-input/ColorInputComponent';
import { DropDownComponent } from '../../../shared/components/dropdown/DropDownComponent';
import { IconsDropdown } from '../../../shared/consts/IconsDropdown';
import { UrlHelper } from '../../../shared/helpers/UrlHelper';
import { LocalButtonStorageService } from '../Helpers/LocalButtonStorageHelper';
import { BuilderModel } from '../Models/BuilderModel';
import { BuilderPreviewScriptApi } from './../Apis/BuilderPreviewScriptApi';
import { BuilderModelFactory } from './../Factories/BuilderModelFactory';
import { BuilderSaveApiRequest } from '../../../shared/Builder/Api/Models/BuilderSaveApiRequest';

@Component({
    selector: 'builder-page',
    standalone: true,
    imports: [DropDownComponent, ColorInputComponent, ...BasePage.BaseImports],
    templateUrl: './BuilderPage.html',
    styleUrl: './BuilderPage.css',
})

export class BuilderPage implements BasePage, OnInit
{
    @ViewChild('previewFrame') previewFrame!: any;

    public error$ = new BehaviorSubject<string | null>(null);
    public loading$ = new BehaviorSubject<boolean>(false);
    public model$ = new BehaviorSubject<BuilderModel | null>(null);
    public icons = IconsDropdown.Options;
    public previewHtml!: SafeHtml;
    public siteId: string | null = null;

    constructor(private sanitizer: DomSanitizer, private builderPreviewScriptApi: BuilderPreviewScriptApi, private router: Router,
        private authenticationService: AuthenticationService, private builderSaveApi: BuilderSaveApi, private route: ActivatedRoute)
    {
        this.siteId = this.route.snapshot.queryParamMap.get('siteId');
    }

    async ngOnInit()
    {
        // When logged in we dont want the user to design a dangling button like after the CTA, instead redirect to their dashboard.
        if (!this.siteId && await this.authenticationService.checkLoginAsync())
        {
            document.location.href = UrlHelper.AppBaseUrl;
        }

        let model = LocalButtonStorageService.Get();

        if (!model)
        {
            model = this.createAndPersistFreshModel()
        }

        await this.updatePreviewAsync(model);

        this.model$.next(model);
    }

    private async updatePreviewAsync(model: BuilderModel)
    {
        const script = await this.builderPreviewScriptApi.Get(model.ButtonForegroundColor, model.ButtonBackgroundColor, model.ButtonIcon);

        const html =
            `<html><head><style>body
            {
                font-family: Arial, sans-serif;
                color: #eee;
                padding: 1rem;
            }
            .bottom-div {
                position: fixed;
                bottom: 10px;
                right: 80px;
                left: 1rem;
            }
            .bottom-div img {
                float: right;
            }
            </style>
            </head>
            <body>
            <h2>Preview</h2>
            <p>This is how your button will show on your site!</p>
            <div class="bottom-div">
            <img class="arrow" src="/assets/img/arrow.png" />
            </div>
            <script>${script}</script>
            </body>
            </html>`;

        this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    }

    private createAndPersistFreshModel(): BuilderModel
    {
        const model = BuilderModelFactory.CreateDefault();

        LocalButtonStorageService.Set(model);

        return model;
    }

    async onChange()
    {
        const model = this.model$.getValue();
        if (model)
        {
            LocalButtonStorageService.Set(model);

            await this.updatePreviewAsync(model);
        }
    }

    async reset()
    {
        if (!confirm('Reset your button configuration?')) return;

        const model = this.createAndPersistFreshModel();

        this.model$.next(model);

        await this.updatePreviewAsync(model);
    }

    async submit(form: NgForm)
    {
        if (form.invalid)
        {
            console.log('invalid form');
            form.control.markAllAsTouched(); // show validation errors
            return;
        }

        if (this.siteId && await this.authenticationService.checkLoginAsync())
        {
            try
            {
                this.loading$.next(true);

                const stored = LocalButtonStorageService.Get();

                if (!stored)
                {
                    this.error$.next("Can't find your button configuration in your browser.");
                    return;
                }

                const saveRequest: BuilderSaveApiRequest = {
                    SiteId: this.siteId,
                    ButtonForegroundColor: stored.ButtonForegroundColor,
                    ButtonBackgroundColor: stored.ButtonBackgroundColor,
                    ButtonIcon: stored.ButtonIcon
                };

                await this.builderSaveApi.ExecuteAsync(saveRequest);

                document.location.href = `${UrlHelper.AppBaseUrl}/sites/${this.siteId}`;
            }
            catch (err: any)
            {
                this.loading$.next(false);
                this.error$.next(err);
            }
        }
        else
        {
            await this.router.navigate(['/builder/register']);
        }
    }
}
